import { useState, useCallback, useRef, useEffect } from "react";
import type {
  Message,
  MessageModality,
  MessageSource,
  WidgetConfig,
  PendingApproval,
  ConversationHandoff,
} from "../../shared/types";
import { ApiClient, type ApiError } from "../api/client";
import {
  EventType,
  type TextMessageStartEvent,
  type ToolCallStartEvent,
  type ToolCallEndEvent,
} from "../api/agui-protocol";
import {
  getConversationId,
  touchConversation,
  getStoredMessages,
  clearConversationData,
  getPendingApprovals,
  setPendingApprovals as persistPendingApprovals,
} from "../utils/storage";
import { trackWidgetEvent, trackError } from "../../telemetry/events";
import { getEnvironmentData } from "../../telemetry/environment";
import { COLLECT_IMAGES_TOOL_NAME } from "../../shared/constants";
import {
  useStatusQueue,
  DEFAULT_STATUS_TIMING,
  type StatusTimingConfig,
} from "./useStatusQueue";
import { useCrossTabSync } from "./useCrossTabSync";
import { useApprovalBuffer } from "./useApprovalBuffer";
export {
  DEFAULT_STATUS_TIMING,
  type StatusTimingConfig,
} from "./useStatusQueue";

type StoredPendingApproval = PendingApproval & { error?: string };

interface UseConversationResult {
  messages: Message[];
  isLoading: boolean;
  statusMessage: string | null;
  conversationId: string | null;
  pendingApproval: StoredPendingApproval | null;
  sendMessage: (
    content: string,
    modality?: MessageModality,
    source?: MessageSource,
  ) => Promise<void>;
  openConversation: (handoff: ConversationHandoff) => void;
  retryMessage: (messageId: string) => Promise<void>;
  submitApproval: (
    decision: "approved" | "denied",
    clientArtifacts?: Record<string, string>,
    toolResult?: string,
  ) => Promise<void>;
  skipApproval: (toolApprovalId: string) => void;
  resetConversation: () => void;
}

export function useConversation(
  widgetConfig: WidgetConfig,
  welcomeMessage?: string | null,
  statusTiming: StatusTimingConfig = DEFAULT_STATUS_TIMING,
): UseConversationResult {
  const {
    statusMessage,
    enqueueStatus,
    clearStatusQueue,
    cancelEscalation,
    scheduleEscalation,
  } = useStatusQueue(statusTiming);
  const [messages, setMessages] = useState<Message[]>(
    () => getStoredMessages(widgetConfig.agentId) ?? [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(() =>
    getConversationId(widgetConfig.agentId),
  );
  const [pendingApprovals, setPendingApprovals] = useState<
    StoredPendingApproval[]
  >(() => getPendingApprovals(widgetConfig.agentId));

  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const pendingRetryRef = useRef<string | null>(null);
  const pendingApprovalRef = useRef(false);
  const conversationGenerationRef = useRef(0);
  const streamingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const welcomeMessageInjected = useRef(false);

  const { handleToolApprovalRequested, reset: resetApprovalBuffer } =
    useApprovalBuffer(setPendingApprovals);

  const {
    handleThreadId,
    conversationIdRef,
    reset: resetCrossTab,
  } = useCrossTabSync({
    agentId: widgetConfig.agentId,
    messagesRef,
    messages,
    pendingApprovals,
    conversationId,
    setMessages,
    setConversationId,
    setPendingApprovals,
    onClear: () => {
      welcomeMessageInjected.current = false;
    },
  });

  useEffect(() => {
    const stored = getStoredMessages(widgetConfig.agentId);
    if (stored && stored.length > 0) {
      welcomeMessageInjected.current = true;
    }
  }, [widgetConfig.agentId]);

  useEffect(() => {
    if (
      welcomeMessage &&
      !welcomeMessageInjected.current &&
      messages.length === 0
    ) {
      const welcomeMsg: Message = {
        id: "welcome-message",
        role: "assistant",
        content: welcomeMessage,
        contentFormat: "html",
        timestamp: Date.now(),
      };
      setMessages([welcomeMsg]);
      welcomeMessageInjected.current = true;
    }
  }, [welcomeMessage, messages.length]);

  useEffect(() => {
    return () => {
      if (streamingTimeoutRef.current) {
        clearTimeout(streamingTimeoutRef.current);
      }
      clearStatusQueue();
    };
  }, [clearStatusQueue]);

  useEffect(() => {
    persistPendingApprovals(widgetConfig.agentId, pendingApprovals);
  }, [pendingApprovals, widgetConfig.agentId]);

  const handleToolCallStatusEvent = useCallback(
    (event: ToolCallStartEvent | ToolCallEndEvent) => {
      if (event.type === EventType.TOOL_CALL_START) {
        cancelEscalation(); // a second tool call must not inherit a stale escalation timer from the previous one
        enqueueStatus("Fetching official resources");
      } else if (event.type === EventType.TOOL_CALL_END) {
        enqueueStatus("Analyzing results", () => {
          scheduleEscalation("Reviewing the details");
          setPendingApprovals([]);
        });
      }
    },
    [cancelEscalation, enqueueStatus, scheduleEscalation],
  );

  const makeChunkProcessor = useCallback(
    (streamingMessageId: string) => {
      const chunkQueue: string[] = [];
      let isProcessing = false;

      const processQueue = () => {
        if (chunkQueue.length === 0) {
          isProcessing = false;
          streamingTimeoutRef.current = null;
          return;
        }

        isProcessing = true;
        const chunk = chunkQueue.shift()!;

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === streamingMessageId
              ? { ...msg, content: msg.content + chunk }
              : msg,
          ),
        );

        streamingTimeoutRef.current = setTimeout(processQueue, 30);
      };

      const enqueueChunk = (chunk: string) => {
        chunkQueue.push(chunk);
        if (!isProcessing) {
          processQueue();
        }
      };

      return enqueueChunk;
    },
    [setMessages],
  );

  const applyMessageStartMetadata = useCallback(
    (streamingMessageId: string, event: TextMessageStartEvent) => {
      if (!event.contentFormat) {
        return;
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === streamingMessageId
            ? { ...msg, contentFormat: event.contentFormat }
            : msg,
        ),
      );
    },
    [],
  );

  const sendMessage = useCallback(
    async (
      content: string,
      modality?: MessageModality,
      source?: MessageSource,
    ) => {
      if (!content.trim()) return;

      if (streamingTimeoutRef.current) {
        clearTimeout(streamingTimeoutRef.current);
        streamingTimeoutRef.current = null;
      }

      clearStatusQueue();
      enqueueStatus("Thinking...");

      const userMessage: Message = {
        id: `user-${Date.now()}`,
        role: "user",
        content: content.trim(),
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);

      touchConversation(widgetConfig.agentId);

      const messageSentAttributes: Record<string, string | number> = {
        messageId: userMessage.id,
        contentLength: content.trim().length,
        sourceUrl: widgetConfig.sourceUrl || "unknown",
        agentId: widgetConfig.agentId,
      };
      if (conversationId) {
        messageSentAttributes.conversationId = conversationId;
      }
      if (widgetConfig.organizationId) {
        messageSentAttributes.organizationId = widgetConfig.organizationId;
      }
      if (source) {
        messageSentAttributes.source = source;
      }
      trackWidgetEvent("message.sent", messageSentAttributes);

      const streamingMessageId = `assistant-${Date.now()}`;
      const streamingMessage: Message = {
        id: streamingMessageId,
        role: "assistant",
        content: "",
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, streamingMessage]);

      let responseStartTracked = false;
      const responseStartTime = Date.now();
      const enqueueChunk = makeChunkProcessor(streamingMessageId);
      const requestConversationId = conversationIdRef.current;
      const requestGeneration = conversationGenerationRef.current;

      try {
        const apiClient = new ApiClient(
          widgetConfig.apiUrl,
          widgetConfig.agentId,
        );
        const handleResponseThreadId = (threadId: string) => {
          if (conversationIdRef.current === requestConversationId) {
            handleThreadId(threadId);
          }
        };

        const response = await apiClient.sendMessage(
          content,
          requestConversationId,
          (chunk) => {
            if (!responseStartTracked) {
              enqueueStatus(null);
              const responseStartAttributes: Record<string, string> = {
                messageId: streamingMessageId,
                sourceUrl: widgetConfig.sourceUrl || "unknown",
                agentId: widgetConfig.agentId,
              };
              if (conversationId) {
                responseStartAttributes.conversationId = conversationId;
              }
              if (widgetConfig.organizationId) {
                responseStartAttributes.organizationId =
                  widgetConfig.organizationId;
              }
              trackWidgetEvent(
                "message.response.start",
                responseStartAttributes,
              );
              responseStartTracked = true;
            }

            enqueueChunk(chunk);
          },
          handleToolCallStatusEvent,
          handleToolApprovalRequested,
          handleResponseThreadId,
          (event) => applyMessageStartMetadata(streamingMessageId, event),
          modality,
        );

        // The synthetic ID keeps streaming state consistent during the response.
        // We defer the swap until streaming completes because chunk processors
        // and abort logic key off the synthetic ID throughout. If the synthetic
        // ID reached TTS instead of the real GUID, the speak endpoint would
        // reject it with a 400 error.
        if (response.messageId) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === streamingMessageId
                ? { ...msg, id: response.messageId! }
                : msg,
            ),
          );
        }

        if (
          requestConversationId === null &&
          response.conversationId &&
          conversationIdRef.current === response.conversationId
        ) {
          const envData = getEnvironmentData();
          const conversationCreatedAttributes: Record<
            string,
            string | number | boolean
          > = {
            conversationId: response.conversationId,
            // Include firstMessageId to link the first message (sent before conversationId existed)
            // This allows App Insights queries to find all messages by checking:
            // conversationId == X OR messageId == firstMessageId
            firstMessageId: userMessage.id,
            sourceUrl: widgetConfig.sourceUrl || "unknown",
            agentId: widgetConfig.agentId,
            ...envData,
          };
          if (widgetConfig.organizationId) {
            conversationCreatedAttributes.organizationId =
              widgetConfig.organizationId;
          }
          trackWidgetEvent(
            "conversation.created",
            conversationCreatedAttributes,
          );
        }

        const responseEndAttributes: Record<string, string | number> = {
          messageId: response.messageId ?? streamingMessageId,
          durationMs: Date.now() - responseStartTime,
          sourceUrl: widgetConfig.sourceUrl || "unknown",
          agentId: widgetConfig.agentId,
        };
        const finalConversationId = response.conversationId || conversationId;
        if (finalConversationId) {
          responseEndAttributes.conversationId = finalConversationId;
        }
        if (widgetConfig.organizationId) {
          responseEndAttributes.organizationId = widgetConfig.organizationId;
        }
        trackWidgetEvent("message.response.end", responseEndAttributes);
      } catch (err) {
        if (conversationGenerationRef.current === requestGeneration) {
          // Remove the streaming message
          setMessages((prev) =>
            prev.filter((msg) => msg.id !== streamingMessageId),
          );

          const apiError = err as ApiError;

          if (
            apiError.statusCode === 409 &&
            Array.isArray(apiError.pendingApprovals) &&
            apiError.pendingApprovals.length > 0
          ) {
            const contextMessage: Message = {
              id: `approval-context-${Date.now()}`,
              role: "assistant",
              content:
                "Before I can continue, I need your approval for the following action. Once you've decided, please resend your message.",
              timestamp: Date.now(),
            };
            setMessages((prev) => [...prev, contextMessage]);
            const incoming = apiError.pendingApprovals;
            setPendingApprovals((prev) => {
              const merged = [...prev];
              for (const approval of incoming) {
                if (
                  !merged.some(
                    (a) => a.toolApprovalId === approval.toolApprovalId,
                  )
                ) {
                  merged.push(approval);
                }
              }
              return merged;
            });
          } else {
            const errorMessage: Message = {
              id: `error-${Date.now()}`,
              role: "assistant",
              content:
                apiError.userMessage ||
                "Something went wrong. Please try again.",
              timestamp: Date.now(),
              error: true,
              errorType: apiError.errorType || "unknown",
              retryable: apiError.retryable !== false,
              originalUserMessage: content.trim(),
            };

            setMessages((prev) => [...prev, errorMessage]);

            const errorAttributes: Record<string, string> = {
              source: "message_send",
              messageId: userMessage.id,
              agentId: widgetConfig.agentId,
              errorType: apiError.errorType || "unknown",
            };
            if (conversationId) {
              errorAttributes.conversationId = conversationId;
            }
            if (widgetConfig.organizationId) {
              errorAttributes.organizationId = widgetConfig.organizationId;
            }
            trackError(err, errorAttributes);
          }
        }
      } finally {
        if (conversationGenerationRef.current === requestGeneration) {
          clearStatusQueue();
          setIsLoading(false);
        }
      }
    },
    [
      clearStatusQueue,
      enqueueStatus,
      handleToolCallStatusEvent,
      handleToolApprovalRequested,
      handleThreadId,
      makeChunkProcessor,
      applyMessageStartMetadata,
      conversationId,
      widgetConfig.agentId,
      widgetConfig.apiUrl,
      widgetConfig.organizationId,
      widgetConfig.sourceUrl,
    ],
  );

  const openConversation = useCallback(
    (handoff: ConversationHandoff) => {
      if (!handoff.conversationId || handoff.messages.length === 0) return;

      conversationGenerationRef.current += 1;
      clearStatusQueue();
      setIsLoading(false);
      resetApprovalBuffer();
      setPendingApprovals([]);
      welcomeMessageInjected.current = true;
      handleThreadId(handoff.conversationId, handoff.messages);
      setMessages(handoff.messages);
      touchConversation(widgetConfig.agentId);
    },
    [
      clearStatusQueue,
      handleThreadId,
      resetApprovalBuffer,
      widgetConfig.agentId,
    ],
  );

  const retryMessage = useCallback(
    async (messageId: string) => {
      const errorMessage = messages.find(
        (msg) => msg.id === messageId && msg.error,
      );
      if (!errorMessage || !errorMessage.originalUserMessage) {
        return;
      }

      if (pendingRetryRef.current === messageId) {
        return;
      }

      pendingRetryRef.current = messageId;

      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));

      try {
        await sendMessage(errorMessage.originalUserMessage);
      } finally {
        pendingRetryRef.current = null;
      }
    },
    [messages, sendMessage],
  );

  const submitApproval = useCallback(
    async (
      decision: "approved" | "denied",
      clientArtifacts?: Record<string, string>,
      toolResult?: string,
    ) => {
      if (pendingApprovals.length === 0 || pendingApprovalRef.current) return;

      pendingApprovalRef.current = true;

      const capturedApproval = pendingApprovals[0];
      const { toolApprovalId, conversationId: approvalConversationId } =
        capturedApproval;
      const requestGeneration = conversationGenerationRef.current;
      const isCurrentApprovalConversation = () =>
        conversationGenerationRef.current === requestGeneration &&
        (conversationIdRef.current === null ||
          conversationIdRef.current === approvalConversationId);

      setIsLoading(true);
      clearStatusQueue();
      enqueueStatus("Processing...");

      const streamingMessageId = `assistant-${Date.now()}`;
      const streamingMessage: Message = {
        id: streamingMessageId,
        role: "assistant",
        content: "",
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, streamingMessage]);
      setPendingApprovals((prev) => prev.slice(1));

      const enqueueChunk = makeChunkProcessor(streamingMessageId);

      try {
        const apiClient = new ApiClient(
          widgetConfig.apiUrl,
          widgetConfig.agentId,
        );
        const handleResponseThreadId = (threadId: string) => {
          if (isCurrentApprovalConversation()) {
            handleThreadId(threadId);
          }
        };

        const response = await apiClient.sendApproval(
          approvalConversationId,
          toolApprovalId,
          decision,
          (chunk) => {
            enqueueStatus(null);
            enqueueChunk(chunk);
          },
          handleToolCallStatusEvent,
          handleToolApprovalRequested,
          handleResponseThreadId,
          (event) => applyMessageStartMetadata(streamingMessageId, event),
          clientArtifacts,
          toolResult,
        );

        if (response.messageId) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === streamingMessageId
                ? { ...msg, id: response.messageId! }
                : msg,
            ),
          );
        }

        if (
          response.conversationId &&
          isCurrentApprovalConversation() &&
          response.conversationId !== conversationIdRef.current
        ) {
          handleThreadId(response.conversationId);
        }

        if (!response.message.content) {
          setMessages((prev) =>
            prev.filter((msg) => msg.id !== streamingMessageId),
          );
        }
      } catch (err) {
        if (isCurrentApprovalConversation()) {
          setMessages((prev) =>
            prev.filter((msg) => msg.id !== streamingMessageId),
          );

          const apiError = err as ApiError;

          const invalidSubmissionMessage =
            capturedApproval.toolName === COLLECT_IMAGES_TOOL_NAME
              ? "Your photos could not be validated. Please skip to continue without images."
              : "The submitted information was invalid. Please skip to continue.";

          const errorMessages: Record<number, string> = {
            400: invalidSubmissionMessage,
            404: "This approval request could not be found. Please skip to continue.",
            409: "This action has already been decided. Please skip to continue.",
          };

          const error =
            (apiError.statusCode !== undefined
              ? errorMessages[apiError.statusCode]
              : undefined) ?? "Something went wrong. Please try again.";

          setPendingApprovals((prev) => [
            { ...capturedApproval, error },
            ...prev,
          ]);
        }
      } finally {
        if (isCurrentApprovalConversation()) {
          clearStatusQueue();
          setIsLoading(false);
        }
        pendingApprovalRef.current = false;
      }
    },
    [
      pendingApprovals,
      clearStatusQueue,
      enqueueStatus,
      handleToolCallStatusEvent,
      handleToolApprovalRequested,
      handleThreadId,
      makeChunkProcessor,
      applyMessageStartMetadata,
      widgetConfig.agentId,
      widgetConfig.apiUrl,
    ],
  );

  const skipApproval = useCallback((toolApprovalId: string) => {
    setPendingApprovals((prev) =>
      prev.filter((a) => a.toolApprovalId !== toolApprovalId),
    );
  }, []);

  const resetConversation = useCallback(() => {
    conversationGenerationRef.current += 1;
    clearConversationData(widgetConfig.agentId);
    resetApprovalBuffer();
    resetCrossTab();
  }, [resetApprovalBuffer, resetCrossTab, widgetConfig.agentId]);

  return {
    messages,
    isLoading,
    statusMessage,
    conversationId,
    pendingApproval: pendingApprovals[0] ?? null,
    sendMessage,
    openConversation,
    retryMessage,
    submitApproval,
    skipApproval,
    resetConversation,
  };
}
